import { Empty } from '@allswipes/ui';
import { useEffect } from 'react';
import {
  Navigate, Route, Routes, useLocation, useNavigationType,
} from 'react-router-dom';

import { api } from './api';
import { rememberLast, rememberLink } from './link';
import { Shell } from './components/Shell';
import {
  LoginScreen,
  NotificationsScreen,
  PointsScreen,
  ProfileScreen,
  WelcomeScreen,
} from './screens/Account';
import { CompanyRequestScreen } from './screens/CompanyRequest';
import { GoogleReturnScreen } from './screens/GoogleReturn';
import { ArchiveScreen } from './screens/Archive';
import { BrandScreen } from './screens/Brand';
import { CampaignScreen } from './screens/Campaign';
import { ContestScreen, ContestsScreen } from './screens/Contests';
import { DrawReportScreen } from './screens/DrawReport';
import { BroadcastScreen } from './screens/Broadcast';
import { FeedScreen } from './screens/Feed';
import { DataDealScreen, QuestScreen } from './screens/Quest';
import {
  InviteJoinScreen, InviteScreen, QuizScreen, StreakScreen,
} from './screens/Mechanics';
import { MyEntriesScreen } from './screens/MyEntries';
import { RulesScreen } from './screens/Rules';
import { ScanScreen } from './screens/Scan';
import { VerifierScreen } from './screens/Verifier';

/**
 * Новый экран открывается сверху.
 *
 * Браузер сам прокрутку не сбрасывает: уйдя с ленты из середины списка, человек
 * попадал на страницу акции в её середину — мимо названия, приза и кнопки. На
 * «назад» прокрутка не трогается: там браузер возвращает своё положение, и это
 * как раз то, чего ждут.
 */
function ScrollTop() {
  const { pathname } = useLocation();
  const type = useNavigationType();
  useEffect(() => {
    if (type === 'POP') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, type]);
  return null;
}

/**
 * Ключ канала из адреса — в память сессии.
 *
 * Одно место на всё приложение: ключ приезжает с баннера и должен пережить
 * переходы по кнопкам до самого гашения кода. Ловить его в каждом экране
 * означало бы забыть про следующий — а забытый ключ это несбывшееся
 * обещание, которое человек видел своими глазами.
 */
function LinkCatcher() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const key = new URLSearchParams(search).get('k');
    if (!key) return;
    const campaign = /^\/c\/([^/]+)/.exec(pathname)?.[1];
    if (campaign) rememberLink(campaign, key);
    else rememberLast(key);
  }, [pathname, search]);
  return null;
}

/**
 * Отметка посещения экрана.
 *
 * Приложение рисуется в браузере, и сервер его переходов не видит. Уходит
 * только путь без параметров: в параметрах лежат поиск и фильтры, а это уже
 * не посещаемость. Ни cookie, ни идентификатора — на сервере останется
 * отпечаток, живущий одни сутки.
 */
function Hit() {
  const { pathname } = useLocation();
  useEffect(() => {
    void api.hit('app', pathname);
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <>
      <ScrollTop />
      <LinkCatcher />
      <Hit />
      <Routes>
        <Route path="/" element={<FeedScreen />} />
        <Route path="/live" element={<BroadcastScreen />} />
        <Route path="/archive" element={<ArchiveScreen />} />
        <Route path="/c/:id" element={<CampaignScreen />} />
        {/* Конкурсы живут на /k: короткий адрес, не пересекается с /c акций. */}
        <Route path="/k" element={<ContestsScreen />} />
        <Route path="/k/:id" element={<ContestScreen />} />
        <Route path="/b/:slug" element={<BrandScreen />} />
        <Route path="/scan" element={<ScanScreen />} />
        {/* Механики без кода: вход в них — с экрана акции. */}
        <Route path="/c/:id/quiz" element={<QuizScreen />} />
        <Route path="/c/:id/streak" element={<StreakScreen />} />
        <Route path="/c/:id/invite" element={<InviteScreen />} />
        <Route path="/c/:id/quest" element={<QuestScreen />} />
        <Route path="/c/:id/deal" element={<DataDealScreen />} />
        <Route path="/invite/:code" element={<InviteJoinScreen />} />
        <Route path="/my" element={<MyEntriesScreen />} />
        <Route path="/draw/:id" element={<DrawReportScreen />} />
        <Route path="/verify/:id" element={<VerifierScreen />} />
        <Route path="/rules/:id" element={<RulesScreen />} />
        <Route path="/points" element={<PointsScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/notifications" element={<NotificationsScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/welcome" element={<WelcomeScreen />} />
      <Route path="/auth/google" element={<GoogleReturnScreen />} />
      <Route path="/for-companies" element={<CompanyRequestScreen />} />
        {/* Старые ссылки прототипа вида /app/... ведут в ленту. */}
        <Route path="/app/*" element={<Navigate to="/" replace />} />
        <Route
          path="*"
          element={
            <Shell title="Не найдено">
              <Empty icon="search" title="Такой страницы нет" />
            </Shell>
          }
        />
      </Routes>
    </>
  );
}
